import ReactECharts from 'echarts-for-react';

/**
 * AutonomyLadderChart_2 — "minimal editorial" variant of AutonomyLadderChart.
 *
 * Same data and chart family (grouped horizontal bar) as the original.
 * Opaque white card, generous whitespace, thin strokes, muted slate palette
 * with a single teal accent, small typography, no gradients/glows/shadows.
 * Quiet magazine-infographic feel.
 */

const INK = {
  teal: '#0AB3BF', // single accent: autonomy
  slate: '#94A3B8', // muted series: human review
  slateDeep: '#64748B', // axis text / labels
  hairline: '#E2E8F0', // thin gridlines
  surface: '#FFFFFF',
};

const MODES = ['MANUAL', 'SEMI_AUTO', 'FULL_AUTO'];
const AUTONOMY = [0, 60, 90]; // what the bot acts on its own
const REVIEW = [100, 40, 10]; // what a human still gates

/** ONE shared percentage formatter — tooltip, labels, and axis all use it. */
const formatPct = (v: number) => `${Math.round(v)}%`;

export const AutonomyLadderChart_2 = ({ height = 280 }: { height?: number }) => {
  const option = {
    backgroundColor: INK.surface,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: '#F1F5F9' } },
      backgroundColor: INK.surface,
      borderColor: INK.hairline,
      borderWidth: 1,
      textStyle: { color: INK.slateDeep, fontSize: 12 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const i = params[0].dataIndex;
        return `${MODES[i]}<br/>${params
          .map((p) => `${p.marker} ${p.seriesName}: <b>${formatPct(p.value)}</b>`)
          .join('<br/>')}`;
      },
    },
    legend: {
      top: 12,
      itemWidth: 10,
      itemHeight: 10,
      icon: 'rect',
      textStyle: { color: INK.slateDeep, fontSize: 11 },
      data: ['Autonomy (bot acts)', 'Human review (gates)'],
    },
    grid: { left: 100, right: 56, top: 52, bottom: 40 },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: 'share of actions (%)',
      nameTextStyle: { color: INK.slate, fontSize: 11 },
      axisLabel: { color: INK.slate, fontSize: 11, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: INK.hairline, width: 1 } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category',
      data: MODES,
      axisLine: { lineStyle: { color: INK.hairline, width: 1 } },
      axisLabel: { color: INK.slateDeep, fontSize: 12, fontWeight: 500 },
      axisTick: { show: false },
    },
    series: [
      {
        name: 'Autonomy (bot acts)',
        type: 'bar',
        barWidth: '26%',
        data: AUTONOMY.map((v) => ({ value: v, itemStyle: { color: INK.teal } })),
        label: {
          show: true,
          position: 'right',
          color: INK.slateDeep,
          fontWeight: 500,
          fontSize: 11,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
      {
        name: 'Human review (gates)',
        type: 'bar',
        barWidth: '26%',
        data: REVIEW.map((v) => ({ value: v, itemStyle: { color: INK.slate } })),
        label: {
          show: true,
          position: 'right',
          color: INK.slateDeep,
          fontWeight: 500,
          fontSize: 11,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 bg-white px-6 py-4">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-3 text-xs text-slate-500 text-center">
        Figure 1. The autonomy ladder: from MANUAL (every action gated) through SEMI_AUTO to FULL_AUTO (audit anytime).
        Autonomy rises with earned trust; human review never disappears. Hover for values.
      </p>
    </div>
  );
};
